import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}


// Format numbers for display
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions
): string {
  return new Intl.NumberFormat('es-AR', {
    notation: 'compact',
    maximumFractionDigits: 1,
    ...options,
  }).format(value)
}

// Format percentages
export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`
}

// Format currency (ARS)
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

// Format dates
export function formatDate(
  date: string | Date,
  format: 'short' | 'long' = 'short'
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  if (format === 'short') {
    return dateObj.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    })
  }

  return dateObj.toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

// Format relative time
export function formatRelativeTime(date: string | Date): string {
  const now = new Date()
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000)

  if (diffInSeconds < 60) {
    return 'hace un momento'
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `hace ${diffInMinutes} min`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `hace ${diffInHours}h`
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) {
    return `hace ${diffInDays}d`
  }

  return formatDate(dateObj)
}

// Get experiment status color
export function getStatusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'text-green-600 bg-green-50'
    case 'draft':
      return 'text-gray-600 bg-gray-50'
    case 'paused':
      return 'text-yellow-600 bg-yellow-50'
    case 'completed':
      return 'text-blue-600 bg-blue-50'
    default:
      return 'text-gray-600 bg-gray-50'
  }
}

// Get trend color and icon
export function getTrendProps(value: number): {
  color: string
  icon: 'up' | 'down' | 'neutral'
} {
  if (value > 0) {
    return { color: 'text-green-600', icon: 'up' }
  } else if (value < 0) {
    return { color: 'text-red-600', icon: 'down' }
  } else {
    return { color: 'text-gray-600', icon: 'neutral' }
  }
}

// Generate unique ID
export function generateId(prefix = ''): string {
  const timestamp = Date.now().toString(36)
  const random = Math.random().toString(36).substr(2, 5)
  return `${prefix}${prefix ? '_' : ''}${timestamp}_${random}`
}

// Debounce function
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// Validate email
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

// Validate URL pattern
export function isValidUrlPattern(pattern: string): boolean {
  try {
    // Basic validation for URL patterns
    if (!pattern) return false
    if (pattern === '*') return true

    // Check for valid path patterns
    const isValidPath = /^\/[a-zA-Z0-9/*\-_]*$/.test(pattern)
    return isValidPath
  } catch {
    return false
  }
}

// Calculate confidence interval
export function calculateConfidenceInterval(
  conversions: number,
  sessions: number,
  confidence: number = 0.95
): { lower: number; upper: number } {
  if (sessions === 0) return { lower: 0, upper: 0 }

  const p = conversions / sessions
  const z = confidence === 0.95 ? 1.96 : confidence === 0.99 ? 2.576 : 1.645
  const margin = z * Math.sqrt((p * (1 - p)) / sessions)

  return {
    lower: Math.max(0, p - margin),
    upper: Math.min(1, p + margin),
  }
}

// Deep merge objects
export function deepMerge<T extends Record<string, any>>(
  target: T,
  ...sources: Partial<T>[]
): T {
  if (!sources.length) return target
  const source = sources.shift()

  if (source) {
    for (const key in source) {
      if (
        source[key] &&
        typeof source[key] === 'object' &&
        !Array.isArray(source[key])
      ) {
        if (!target[key]) target[key] = {} as any
        deepMerge(target[key], source[key])
      } else {
        target[key] = source[key] as any
      }
    }
  }

  return deepMerge(target, ...sources)
}
